import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuthStore } from '@/zustand/auth/store';
import { DEVELOPMENT_MODE, LanguageCodes } from '@/constants';
import { jsonTryParse, removeEmojisAndLatex } from '@/utility/index';
import { ConversationLogResponse, StreamManager } from '@/managers/stream-manager';
import { toast } from 'react-toastify';
import { TimeSpan } from '@/utility/time-span';
import { useServerTimeStore } from '@/zustand/server-time/store.ts';

export const useProvider = (controller: any) => {
	const { getData } = useAuthStore();

	const auth = getData().user;

	return useMemo(() => new controller(), [auth]);
};

export const useLoginProvider = (controller: any) => {
	return new controller();
};

export const useAuthProvider = (controller: any) => {
	const { getData } = useAuthStore();

	const auth = getData();

	return new controller(auth?.user?.email);
};

interface SpeechSynthesisResult {
	speakStart: (text: string, messageId: string) => void;
	speakStop: () => Promise<void>;
	speakPause: () => void;
	speakResume: () => void;
	speakToggle: () => void;
	isSpeaking: boolean;
	isActive: boolean;
	isPaused: boolean;
	rate: number;
	setSpeed: (newRate: number) => void;
	currentlyPlayingMessageId: string | null;
	setReadEmojis: (value: boolean) => void;
}

interface SpeechSynthesisProps {
	initialReadEmojis?: boolean;
	initialRate?: number;
}

export const useSpeechSynthesis = ({ initialReadEmojis = false, initialRate = 1.0 }: SpeechSynthesisProps): SpeechSynthesisResult => {
	const [synth, setSynth] = useState<SpeechSynthesis | null>(null);
	const [isSpeaking, setIsSpeaking] = useState<boolean>(false);
	const [isActive, setIsActive] = useState<boolean>(false);
	const [isPaused, setIsPaused] = useState<boolean>(false);
	const [readEmojis, setReadEmojis] = useState<boolean>(initialReadEmojis);
	const [currentlyPlayingMessageId, setCurrentlyPlayingMessageId] = useState<string | null>(null);
	const [rate, setRate] = useState<number>(initialRate);

	useEffect(() => {
		if ('speechSynthesis' in window) {
			setSynth(window.speechSynthesis);
			setIsActive(true);
		} else {
			setIsActive(false);
		}

		return () => {
			if (synth) {
				synth.cancel();
			}
			setIsSpeaking(false);
			setIsActive(false);
			setIsPaused(false);
			setCurrentlyPlayingMessageId(null);
		};
	}, []);

	const speakStart = (text: string, messageId: string): void => {
		if (synth) {
			const processedText = readEmojis ? text : removeEmojisAndLatex(text);
			const utterance = new SpeechSynthesisUtterance(processedText);

			utterance.lang = LanguageCodes.TR;
			utterance.rate = rate;

			const voices = synth.getVoices();
			const turkishVoice = voices.find(voice => voice.lang === LanguageCodes.TR);

			if (turkishVoice) {
				utterance.voice = turkishVoice;
			}

			utterance.onstart = (): void => {
				setIsSpeaking(true);
				setIsActive(true);
				setIsPaused(false);
			};

			utterance.onend = (): void => {
				setIsSpeaking(false);
				setIsActive(false);
				setIsPaused(false);
				setCurrentlyPlayingMessageId(null);
			};

			utterance.onerror = (): void => {
				setIsSpeaking(false);
				setIsActive(false);
				setIsPaused(false);
				setCurrentlyPlayingMessageId(null);
			};

			synth.cancel();
			synth.speak(utterance);

			setIsSpeaking(true);
			setCurrentlyPlayingMessageId(messageId);
		}
	};

	const setSpeed = (newRate: number): void => {
		setRate(newRate);
	};

	const speakStop = async (): Promise<void> => {
		if (synth) {
			synth.cancel();
			setIsSpeaking(false);
			setIsActive(false);
			setIsPaused(false);
			setCurrentlyPlayingMessageId(null);
		}
	};

	const speakPause = (): void => {
		if (synth && isActive && !isPaused) {
			synth.pause();
			setIsSpeaking(false);
			setIsPaused(true);
		}
	};

	const speakResume = (): void => {
		if (synth && isPaused) {
			synth.resume();
			setIsSpeaking(true);
			setIsPaused(false);
		}
	};

	const speakToggle = (): void => {
		if (isActive) {
			if (isPaused) {
				speakResume();
			} else {
				speakPause();
			}
		} else if (currentlyPlayingMessageId) {
			speakResume();
		}
	};

	useEffect(() => {
		const handleVisibilityChange = () => {
			if (document.hidden && window.speechSynthesis) {
				window.speechSynthesis.cancel();
				setIsSpeaking(false);
				setIsPaused(false);
			}
		};

		document.addEventListener('visibilitychange', handleVisibilityChange);

		return () => {
			document.removeEventListener('visibilitychange', handleVisibilityChange);
		};
	}, []);

	return {
		speakStart,
		speakStop,
		speakPause,
		speakResume,
		speakToggle,
		rate,
		isSpeaking,
		isActive,
		isPaused,
		currentlyPlayingMessageId,
		setReadEmojis,
		setSpeed
	};
};

interface UseStreamManagerResponse {
	streamText: string;
	startStreaming: (promptRequestBody: ConversationLogResponse, isRegenerateStream?: boolean) => Promise<void>;
	isStreaming: boolean;
	resetStream: () => void;
	streamConversationId: string | null;
	conversationLogId: string | null;
}

const STREAM_URL = {
	STREAM: 'prompt/stream',
	REGENERATE: 'prompt/regenerate/stream'
};

const REGERENATE_TYPE = {
	REGENERATE: 'REGENERATE',
	CONTINUE: 'CONTINUE'
};

export const useStreamManager = (url: string, accessToken: string): UseStreamManagerResponse => {
	const [streamText, setStreamText] = useState<string>('');
	const [isStreaming, setIsStreaming] = useState<boolean>(false);
	const [streamConversationId, setStreamConversationId] = useState<string | null>(null);
	const [conversationLogId, setConversationLogId] = useState<string | null>(null);
	const isResetRef = useRef(false);

	const startStreaming = useCallback(
		async (promptRequestBody: ConversationLogResponse, isRegenerateStream: boolean = false): Promise<void> => {
			try {
				isResetRef.current = false;
				setStreamText('');
				setIsStreaming(true);
				setStreamConversationId(null);
				setConversationLogId(null);

				const streamUrl = url + (isRegenerateStream ? STREAM_URL.REGENERATE : STREAM_URL.STREAM);

				if (isRegenerateStream) {
					delete promptRequestBody.curriculumId;

					promptRequestBody.generateType = REGERENATE_TYPE.REGENERATE;
				}

				const streamManager: StreamManager = new StreamManager(streamUrl, accessToken);

				const result = await streamManager.sendPrompt(promptRequestBody, async data => {
					if (!isResetRef.current) {
						setStreamText(data);
					}
				});

				if (!isResetRef.current) {
					if (result?.response) {
						setStreamText(result.response);
					}
					setStreamConversationId(result?.conversationId || null);
					setConversationLogId(result?.conversationLogId || null);
				}
			} catch (error: any) {
				if (!isResetRef.current) {
					throw new Error(`Error fetching stream response: ${error.message}`);
				}
			} finally {
				setIsStreaming(false);
			}
		},
		[url, accessToken]
	);

	const resetStream = (): void => {
		isResetRef.current = true;
		setStreamText('');
		setStreamConversationId(null);
		setConversationLogId(null);
		setIsStreaming(false);
	};

	return {
		streamText,
		startStreaming,
		isStreaming,
		conversationLogId,
		streamConversationId,
		resetStream
	};
};

interface SpeechRecognitionHookResult {
	transcript: string;
	interimTranscript: string;
	isListening: boolean;
	recordingTime: number;
	startListening: () => Promise<void>;
	stopListening: () => void;
	pauseListening: () => void;
	resumeListening: () => void;
	toggleListening: () => Promise<void>;
}

interface SpeechRecognitionEvent extends Event {
	resultIndex: number;
	results: SpeechRecognitionResultList;
}

interface SpeechRecognitionResultList {
	length: number;
	item(index: number): SpeechRecognitionResult;
	[index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
	isFinal: boolean;
	[index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionAlternative {
	transcript: string;
	confidence: number;
}

interface SpeechRecognitionErrorEvent extends Event {
	error: string;
	message: string;
}

interface SpeechRecognition extends EventTarget {
	continuous: boolean;
	interimResults: boolean;
	lang: string;
	onresult: (event: SpeechRecognitionEvent) => void;
	onstart: () => void;
	onend: () => void;
	onerror: (event: SpeechRecognitionErrorEvent) => void;
	start: () => void;
	stop: () => void;
}

export const useSpeechRecognition = (): SpeechRecognitionHookResult => {
	const [isListening, setIsListening] = useState<boolean>(false);
	const [isPaused, setIsPaused] = useState<boolean>(false);
	const [transcript, setTranscript] = useState<string>('');
	const [interimTranscript, setInterimTranscript] = useState<string>('');
	const [recordingTime, setRecordingTime] = useState<number>(0);

	const recognitionRef = useRef<SpeechRecognition | null>(null);

	const initializeSpeechRecognition = () => {
		const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

		if (!SpeechRecognition) {
			console.error("Tarayıcınız ses tanıma API'sini desteklemiyor.");
			return;
		}

		const recognition = new SpeechRecognition();

		recognition.continuous = true;
		recognition.interimResults = true;
		recognition.lang = LanguageCodes.TR;

		recognition.onresult = (event: SpeechRecognitionEvent): void => {
			let finalTranscript = '';
			let interimTranscript = '';

			for (let i = event.resultIndex; i < event.results.length; i++) {
				const transcript = event.results[i][0].transcript;

				if (event.results[i].isFinal) {
					finalTranscript += transcript + ' ';
				} else {
					interimTranscript += transcript;
				}
			}
			setTranscript(prev => prev + finalTranscript);
			setInterimTranscript(interimTranscript);
		};

		recognition.onstart = (): void => {
			setIsListening(true);
			setIsPaused(false);
		};

		recognition.onend = (): void => {
			if (!isPaused) {
				setIsListening(false);
			}
		};

		recognition.onerror = (_event: any): void => {
			// console.log("Speech recognition error:", event.error);
			setIsListening(false);
			setIsPaused(false);
		};

		recognitionRef.current = recognition;
	};

	useEffect(() => {
		let interval: NodeJS.Timeout;

		if (isListening && !isPaused) {
			interval = setInterval(() => {
				setRecordingTime(prevTime => prevTime + 1);
			}, TimeSpan.milliSecond.oneThousand);
		}

		return () => clearInterval(interval);
	}, [isListening, isPaused]);

	const checkMicrophonePermission = async (): Promise<boolean> => {
		try {
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			stream.getTracks().forEach(track => track.stop());
			return true;
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
		} catch (err) {
			// console.log("Mikrofon erişim izni alınamadı:", err);
			return false;
		}
	};

	const startListening = useCallback(async (): Promise<void> => {
		const hasPermission = await checkMicrophonePermission();

		if (!hasPermission) {
			toast.info('Dopi ile konuşabilmek için mikrofon erişim izni vermelisin 🚀');
			return;
		}

		if (!recognitionRef.current) {
			initializeSpeechRecognition();
		}

		setTranscript('');
		setInterimTranscript('');
		recognitionRef.current?.start();
	}, []);

	const stopListening = useCallback((): void => {
		recognitionRef.current?.stop();
		setIsListening(false);
		setIsPaused(false);
		setRecordingTime(0);
	}, []);

	const pauseListening = useCallback((): void => {
		if (isListening && !isPaused) {
			recognitionRef.current?.stop();
			setIsPaused(true);
		}
	}, [isListening, isPaused]);

	const resumeListening = useCallback((): void => {
		if (isPaused) {
			recognitionRef.current?.start();
			setIsPaused(false);
		}
	}, [isPaused]);

	const toggleListening = useCallback(async (): Promise<void> => {
		if (isListening) {
			if (isPaused) {
				resumeListening();
			} else {
				pauseListening();
			}
		} else {
			await startListening();
		}
	}, [isListening, isPaused, resumeListening, pauseListening, startListening]);

	return {
		transcript,
		interimTranscript,
		isListening,
		recordingTime,
		startListening,
		stopListening,
		pauseListening,
		resumeListening,
		toggleListening
	};
};

export const useGetAiToolsModalProps = () => {
	const props = localStorage.getItem('props');
	const _props = jsonTryParse(props!);

	return {
		_props: _props,
		toolId: _props?.toolId,
		values: _props?.values,
		namedValues: _props?.namedValues,
		maxWaitingTime: _props?.maxWaitingTime,
		name: _props?.name,
		configurationId: _props?.id,
		viewType: _props?.viewType,
		transactionId: _props?.transactionId
	};
};

export const deepEqual = (object1: any, object2: any): boolean => {
	if (object1 === object2) return true;

	if (object1 === null || object2 === null) return object1 === object2;

	const type1 = typeof object1;
	const type2 = typeof object2;
	if (type1 !== type2) return false;

	if (Array.isArray(object1) && Array.isArray(object2)) {
		if (object1.length !== object2.length) return false;
		return object1.every((item, index) => deepEqual(item, object2[index]));
	}

	if (type1 === 'object') {
		const keys1 = Object.keys(object1);
		const keys2 = Object.keys(object2);

		if (keys1.length !== keys2.length) return false;

		const keySet = new Set(keys1);
		if (!keys2.every(key => keySet.has(key))) return false;

		return keys1.every(key => deepEqual(object1[key], object2[key]));
	}

	return object1 === object2;
};

interface ContentObject {
	title?: string;
	content?: string;
	[key: string]: any;
}

export const compareContent = (obj1: ContentObject, obj2: ContentObject): boolean => {
	const differences = [];

	const allKeys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);

	for (const key of allKeys) {
		if (!(key in obj1)) {
			differences.push({
				field: key,
				type: 'missing',
				value2: obj2[key]
			});
			continue;
		}

		if (!(key in obj2)) {
			differences.push({
				field: key,
				type: 'missing',
				value1: obj1[key]
			});
			continue;
		}

		const value1 = obj1[key];
		const value2 = obj2[key];

		const normalizeContent = (content: string): string => {
			return content
				.replace(/\s+/g, ' ')
				.trim()
				.replace(/[\n\r]/g, '')
				.replace(/['"]([^'"]*)['"]/g, '"$1"')
				.replace(/\s+>/g, '>')
				.replace(/>\s+</g, '><')
				.toLowerCase();
		};

		if (typeof value1 === 'string' && typeof value2 === 'string') {
			const normalizedValue1 = normalizeContent(value1);
			const normalizedValue2 = normalizeContent(value2);

			if (normalizedValue1 !== normalizedValue2) {
				differences.push({
					field: key,
					type: 'changed',
					value1: value1,
					value2: value2
				});
			}
		} else if (value1 !== value2) {
			differences.push({
				field: key,
				type: 'changed',
				value1: value1,
				value2: value2
			});
		}
	}

	return differences.length === 0;
};

interface SendSmsParams {
	name: string;
	surname: string;
	phoneNumber: string;
	startDate?: string;
	expireDate?: string;
}

export const useSendSms = () => {
	const sendSms = useCallback(async ({ name, surname, phoneNumber, startDate = '', expireDate = '' }: SendSmsParams) => {
		try {
			const cleanPhoneNumber = phoneNumber.replace(/\D/g, '');

			const response = await fetch(process.env.SMTP_SERVER_URL!, {
				method: 'POST',
				body: JSON.stringify({
					USERNAME: process.env.SMTP_SERVER_USERNAME!,
					PASSWORD: process.env.SMTP_SERVER_PASSWORD!,
					ORIGINATOR: process.env.SMTP_SERVER_ORIGINATOR!,
					MULTI: 'IYS',
					NAME: name,
					SURNAME: surname,
					STARTDATE: startDate,
					EXPIREDATE: expireDate,
					PHONENUMBER: cleanPhoneNumber
				})
			});

			if (!response.ok && DEVELOPMENT_MODE) {
				console.log('SMS gönderilemedi');
			}
		} catch (error) {
			if (error && DEVELOPMENT_MODE) {
				console.error('SMS gönderimi sırasında hata:', error);
			}
		}
	}, []);

	return { sendSms };
};

export const useCountdown = (milliseconds: number) => {
	const totalSeconds = Math.floor(milliseconds / 1000);
	const initialMinutes = Math.floor(totalSeconds / 60);
	const initialSeconds = totalSeconds % 60;

	const [minutes, setMinutes] = useState(initialMinutes);
	const [seconds, setSeconds] = useState(initialSeconds);
	const [isRunning, setIsRunning] = useState(false);
	const [isCompleted, setIsCompleted] = useState(false);

	const remainingSeconds = minutes * 60 + seconds;

	useEffect(() => {
		let interval: any = null;

		if (isRunning) {
			interval = setInterval(() => {
				if (minutes === 0 && seconds === 1) {
					setIsRunning(false);
					setIsCompleted(true);
					setSeconds(0);
					clearInterval(interval);
				} else if (seconds === 0) {
					setMinutes(prev => prev - 1);
					setSeconds(59);
				} else {
					setSeconds(prev => prev - 1);
				}
			}, 1000);
		}

		return () => {
			if (interval) clearInterval(interval);
		};
	}, [isRunning, minutes, seconds]);

	const start = () => {
		setIsRunning(true);
		setIsCompleted(false);
	};

	const completed = () => {
		setIsRunning(false);
		setIsCompleted(true);
	};

	const pause = () => {
		setIsRunning(false);
	};

	const reset = (newMilliseconds = milliseconds) => {
		const newTotalSeconds = Math.floor(newMilliseconds / 1000);
		const newMinutes = Math.floor(newTotalSeconds / 60);
		const newSeconds = newTotalSeconds % 60;

		setIsRunning(true);
		setIsCompleted(false);
		setMinutes(newMinutes);
		setSeconds(newSeconds);
	};

	const formatTime = (time: number) => {
		return time.toString().padStart(2, '0');
	};

	return {
		minutes,
		seconds,
		remainingSeconds,
		isRunning,
		isCompleted,
		formattedTime: `${formatTime(minutes)}:${formatTime(seconds)}`,
		start,
		pause,
		reset,
		completed
	};
};

export const useAccurateCountdown = (initialTargetDate?: string | Date) => {
	const [targetDate, setTargetDate] = useState<Date | undefined>(initialTargetDate ? new Date(initialTargetDate) : undefined);
	const [remainingSeconds, setRemainingSeconds] = useState(0);
	const [isRunning, setIsRunning] = useState(false);
	const [isCompleted, setIsCompleted] = useState(!initialTargetDate);
	const serverTimeRef = useRef<Date | null>(null);

	const { data: serverTimeData } = useServerTimeStore();

	const intervalRef = useRef<NodeJS.Timeout | null>(null);

	const calculateRemaining = (target: Date) => {
		const now = new Date(serverTimeRef.current);
		const diff = Math.floor((target.getTime() - now.getTime()) / 1000);
		return diff > 0 ? diff : 0;
	};

	useEffect(() => {
		serverTimeRef.current = serverTimeData?.serverTime || new Date();
	}, [serverTimeData]);

	const clearTimer = () => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}
	};

	const tick = () => {
		if (!targetDate) return;

		const remaining = calculateRemaining(targetDate);
		setRemainingSeconds(remaining);

		if (remaining <= 0) {
			setIsCompleted(true);
			setIsRunning(false);
			clearTimer();
		}
	};

	useEffect(() => {
		if (isRunning && targetDate) {
			tick();
			intervalRef.current = setInterval(tick, 1000);
		}

		return () => clearTimer();
	}, [isRunning, targetDate]);

	const start = () => {
		if (targetDate) {
			setIsCompleted(false);
			setIsRunning(true);
		}
	};

	const reset = (newTargetDate: string | Date) => {
		const date = new Date(newTargetDate);
		setTargetDate(date);
		setRemainingSeconds(calculateRemaining(date));
		setIsCompleted(false);
		setIsRunning(true);
	};

	const pause = () => {
		setIsRunning(false);
		clearTimer();
	};

	const formatTime = (time: number) => time.toString().padStart(2, '0');

	const minutes = Math.floor(remainingSeconds / 60);
	const seconds = remainingSeconds % 60;

	return {
		minutes,
		seconds,
		remainingSeconds,
		formattedTime: `${formatTime(minutes)}:${formatTime(seconds)}`,
		isRunning,
		isCompleted,
		start,
		pause,
		reset
	};
};

export const useFileUpload = () => {
	const uploadFile = async (file?: File, uploadUrl?: string): Promise<Response | Error | null> => {
		if (file && uploadUrl) {
			return await fetch(uploadUrl, {
				method: 'PUT',
				body: new Blob([file], { type: file?.type }),
				headers: {
					'Content-Type': file?.type,
					'Content-Length': file?.size?.toString()
				}
			}).catch(error => error);
		}

		return null;
	};

	return { uploadFile };
};

const useLocalStorage = (key: string, defaultValue: unknown) => {
	const [value, setValue] = useState(() => {
		let currentValue;

		try {
			currentValue = JSON.parse(localStorage.getItem(key) || String(defaultValue));
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
		} catch (error) {
			currentValue = defaultValue;
		}

		return currentValue;
	});

	useEffect(() => {
		localStorage.setItem(key, JSON.stringify(value));
	}, [value, key]);

	return [value, setValue];
};

export default useLocalStorage;
